<div class="mx-auto w-full max-w-4xl">
  @if (uploading()) {
    <div class="mb-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 shadow-sm">
      <div class="flex items-center gap-2 mb-1">
        <div
          class="w-4 h-4 border-2 border-sky-400 border-t-transparent rounded-full animate-spin"
        ></div>
        <span class="text-xs font-medium text-sky-700">Subiendo archivo...</span>
        <span class="text-xs text-sky-500 ml-auto">{{ uploadProgress() }}%</span>
      </div>
      <div class="h-1.5 w-full bg-sky-200 rounded-full overflow-hidden">
        <div
          class="h-full bg-sky-500 rounded-full transition-all duration-300"
          [style.width.%]="uploadProgress()"
        ></div>
      </div>
    </div>
  }

  @if (recordingState() === 'recording') {
    <div class="mb-2 animate-[fadeIn_0.2s_ease-out]">
      <div class="relative overflow-hidden rounded-2xl border border-red-200 bg-white shadow-lg">
        <div class="flex items-center gap-4 px-4 py-4">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
            <span class="relative flex h-3 w-3">
              <span
                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
              ></span>
              <span class="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
          </div>

          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-slate-800">Grabando nota de voz</p>
            <p class="text-lg font-mono tabular-nums font-bold text-red-600">
              {{ formatTime(tiempoGrabacion()) }}
            </p>
          </div>

          <div class="hidden sm:flex flex-1 items-center justify-center gap-0.5 h-10">
            @for (height of bars(); track $index) {
              <div
                class="w-0.5 rounded-full"
                [class.bg-red-400]="$index % 3 === 0"
                [class.bg-red-300]="$index % 3 !== 0"
                [class.animate-pulse]="true"
                [style.height.px]="height + 8"
                [style.animation-delay.ms]="$index * 50"
              ></div>
            }
          </div>

          <button
            type="button"
            class="shrink-0 rounded-full bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-600 active:scale-95"
            (click)="toggleRecording()"
          >
            <i class="bi bi-stop-fill mr-1"></i> Detener
          </button>
        </div>

        <div
          class="h-1 bg-red-500 transition-all duration-1000 ease-linear"
          [style.width.%]="(tiempoGrabacion() / maxDuracionGrabacion) * 100"
        ></div>
      </div>
    </div>
  }

  @if (attachmentPreview(); as preview) {
    <div class="mb-2 animate-[fadeIn_0.2s_ease-out]">
      <div class="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
        <button
          type="button"
          class="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-slate-800/60 text-white shadow transition hover:bg-slate-800/80"
          aria-label="Quitar archivo"
          (click)="cancelarAttachment()"
          [disabled]="uploading()"
        >
          <i class="bi bi-x-lg text-sm"></i>
        </button>

        @switch (preview.tipo) {
          @case (MicaChat_TipoMensaje.IMAGE) {
            <img [src]="preview.blobUrl" class="h-48 w-full object-cover" alt="Preview" />
          }
          @case (MicaChat_TipoMensaje.AUDIO) {
            <div class="flex items-center gap-4 px-4 py-5">
              <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-100"
              >
                <i class="bi bi-mic-fill text-xl text-sky-600"></i>
              </div>
              <div class="flex-1 min-w-0">
                <audio #audioPreview controls class="w-full h-10"></audio>
              </div>
            </div>
          }
          @case (MicaChat_TipoMensaje.VIDEO) {
            <div class="flex items-center gap-4 px-4 py-5">
              <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-purple-100"
              >
                <i class="bi bi-film text-xl text-purple-600"></i>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-slate-800 truncate">{{ preview.fileName }}</p>
                <p class="text-xs text-slate-400">Video</p>
              </div>
            </div>
          }
          @case (MicaChat_TipoMensaje.DOCUMENT) {
            <div class="flex items-center gap-4 px-4 py-5">
              <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-100"
              >
                <i class="bi bi-file-earmark text-xl text-orange-600"></i>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-slate-800 truncate">{{ preview.fileName }}</p>
                <p class="text-xs text-slate-400">Documento</p>
              </div>
            </div>
          }
          @default {
            <div class="flex items-center gap-4 px-4 py-5">
              <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-100"
              >
                <i class="bi bi-paperclip text-xl text-slate-500"></i>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-slate-800 truncate">{{ preview.fileName }}</p>
                <p class="text-xs text-slate-400">Archivo</p>
              </div>
            </div>
          }
        }

        <div class="flex items-center gap-2 border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400 flex-1">
            {{
              permitidoTextoEnAttachment() === false
                ? 'No se puede enviar texto junto con este tipo de archivo'
                : messageDraft().trim()
                  ? 'Se enviará junto con tu texto'
                  : 'Escribe un texto para acompañar (opcional)'
            }}
          </p>
          <button
            type="button"
            class="rounded-full border border-slate-300 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100 disabled:opacity-50"
            (click)="cancelarAttachment()"
            [disabled]="uploading()"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="rounded-full bg-sky-600 px-5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
            (click)="enviarAttachment()"
            [disabled]="uploading()"
          >
            Enviar
          </button>
        </div>
      </div>
    </div>
  }

  <div
    class="rounded-[1.35rem] border border-slate-200 bg-white px-2 py-2 shadow-sm"
    [class.border-sky-400]="isDragOver()"
    [class.ring-2]="isDragOver()"
    [class.ring-sky-100]="isDragOver()"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
  >
    <div class="relative flex items-center gap-1.5 sm:gap-2">
      <div
        class="flex flex-1 items-center gap-1 rounded-[1.15rem] border border-slate-200 bg-slate-50 px-2.5 py-1.5 focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100"
      >
        <div #emojiMenu class="relative">
          <button
            type="button"
            class="vc-emoji-safe rounded-full text-3xl text-slate-500 transition-all duration-200 hover:bg-white hover:scale-100 hover:animate-bounce"
            aria-label="Emojis"
            (click)="toggleEmojiPicker()"
          >
            😊
          </button>
          @if (showEmojiPicker()) {
            <div
              class="absolute bottom-12 left-0 z-30 grid w-56 grid-cols-6 gap-1 rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
            >
              @for (emoji of emojis; track $index) {
                <button
                  type="button"
                  class="vc-emoji-safe rounded-md p-1.5 text-lg transition hover:bg-slate-100"
                  (click)="insertEmoji(emoji)"
                >
                  {{ emoji }}
                </button>
              }
            </div>
          }
        </div>

        <button
          type="button"
          class="vc-emoji-safe shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:text-sky-600 hover:bg-white"
          aria-label="Adjuntar archivo"
          (click)="triggerFilePicker()"
        >
          <i class="bi bi-paperclip text-lg"></i>
        </button>

        <input
          #fileInput
          type="file"
          class="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
          (change)="onFileSelected($event)"
        />

        <textarea
          #composer
          class="max-h-32 min-h-10 w-full resize-none bg-transparent py-1 text-sm text-slate-700 outline-none placeholder:text-slate-400"
          rows="1"
          [placeholder]="
            isDragOver()
              ? 'Suelta el archivo aquí...'
              : attachmentPreview()
                ? 'Añade un texto...'
                : 'Escribe un mensaje'
          "
          (input)="onInput(composer)"
          (keydown)="onComposerKeydown($event)"
          [disabled]="permitidoTextoEnAttachment() === false || uploading()"
        ></textarea>
      </div>

      @if (attachmentPreview()) {
        <button
          type="button"
          class="shrink-0 self-center rounded-full bg-sky-600 px-3 py-2 text-white shadow-sm transition hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Enviar"
          (click)="enviarAttachment()"
          [disabled]="uploading()"
        >
          <i class="bi bi-send-fill text-sm"></i>
        </button>
      } @else if (messageDraft().trim().length > 0) {
        <button
          type="button"
          class="shrink-0 self-center rounded-full bg-sky-600 px-3 py-2 text-white shadow-sm transition hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Enviar"
          (click)="sendMessage()"
          [disabled]="uploading()"
        >
          <i class="bi bi-send-fill text-sm"></i>
        </button>
      } @else {
        <button
          type="button"
          class="shrink-0 self-center rounded-full px-3 py-2 text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          [class.bg-sky-600]="recordingState() === 'idle'"
          [class.hover:bg-sky-500]="recordingState() === 'idle'"
          [class.bg-red-500]="recordingState() === 'recording'"
          [class.animate-pulse]="recordingState() === 'recording'"
          [attr.aria-label]="
            recordingState() === 'recording' ? 'Detener grabación' : 'Grabar nota de voz'
          "
          (click)="toggleRecording()"
          [disabled]="uploading()"
        >
          @if (recordingState() === 'recording') {
            <i class="bi bi-stop-fill text-sm"></i>
          } @else {
            <i class="bi bi-mic-fill text-sm"></i>
          }
        </button>
      }
    </div>
  </div>
</div>
